import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { DEFAULT_LANGUAGE, LANGUAGES, translate } from "./messages";

const STORAGE_KEY = "language";

// German-speaking browsers get the German interface, everyone else
// English - unless the language was switched by hand, which is
// remembered per browser like the sound settings.
export function detectLanguage() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable (e.g. private browsing) - use the browser's
  }
  const browser = (navigator.languages || [navigator.language])
    .filter(Boolean)
    .map((tag) => tag.toLowerCase().slice(0, 2))
    .find((tag) => LANGUAGES.includes(tag));
  return browser || DEFAULT_LANGUAGE;
}

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key, params) => translate(DEFAULT_LANGUAGE, key, params),
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectLanguage);

  // Screen readers, hyphenation and the browser's translate offer all
  // read the page language from <html lang>.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((value) => {
    setLanguageState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // keep it in memory only
    }
  }, []);

  const t = useCallback(
    (key, params) => translate(language, key, params),
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
